/**
 * Who is signed in, which agency they are acting in, and what they may do.
 *
 * Fetched once on boot from `/auth/me` and held in context. It carries the role, the plan and
 * the agency's own vocabulary, so every screen can label itself and hide what the user cannot
 * do without a second round trip.
 *
 * Permission checks here are for the *interface* only — hiding a button the user cannot use.
 * The API enforces the same rules independently, because a hidden button is not a permission.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_VOCABULARY, type ModuleCode, type Restriction } from "@rentorng/core";
import type { Vocabulary } from "@rentorng/core";
import { can as roleCan, type Permission, type Role } from "@rentorng/core/permissions";
import { api } from "./api";

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  isPlatformAdmin: boolean;
  /** Confirmed address. False holds the agency's outbox — see ADR-0085. */
  emailVerified: boolean;
}

export interface SessionOrg {
  id: string;
  name: string;
  slug: string;
  currency: string;
  timezone: string;
  graceDays: number;
  dueSoonDays: number;
  vocabulary: Record<string, string> | null;
  logoUrl: string | null;
  /** How much of a property address the public listing shows: `full` or `area`. */
  publicAddress: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  invoicePrefix: string;
  receiptPrefix: string;
  receiptFooter: string | null;
  showBalanceOnReceipt: boolean;
  autoRemindersEnabled: boolean;
  sendFromHour: number;
  sendToHour: number;
}

export interface PlanState {
  code: "starter" | "growth" | "agency" | "enterprise";
  status: string;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
}

export interface RestrictionNotice {
  headline: string;
  detail: string;
}

export interface MeResponse {
  user: SessionUser | null;
  org?: SessionOrg | null;
  role?: Role | null;
  portfolio?: string[] | null;
  orgs?: Array<{ id: string; name: string; slug: string; role: Role }>;
  vocabulary?: Vocabulary;
  plan?: PlanState | null;
  restriction?: Restriction;
  restrictionNotice?: RestrictionNotice | null;
  listingsUrl?: string;
  /** Present only for platform staff. Absent for everybody else, deliberately. */
  consoleUrl?: string;
  /** What this organisation can open: what it switched on, narrowed by what it pays for. */
  modules?: ModuleCode[];
}

interface SessionValue {
  loading: boolean;
  user: SessionUser | null;
  org: SessionOrg | null;
  role: Role | null;
  orgs: Array<{ id: string; name: string; slug: string; role: Role }>;
  plan: PlanState | null;
  /** Where the public listing site lives, as the deployment reported it. */
  listingsUrl: string;
  /** Where the console lives — empty unless this person is platform staff. */
  consoleUrl: string;
  /**
   * The modules this organisation has. Always at least `letting`.
   *
   * Read it to decide what to *draw*. The API refuses independently — an organisation without a
   * module gets a 402 that names it, whatever the rail happens to show.
   */
  modules: ModuleCode[];
  vocab: Vocabulary;
  /**
   * What a suspension allows. `level: "none"` for very nearly everybody.
   *
   * Read it to explain; do not read it to decide — `can()` already accounts for it, and a screen
   * that checks the level itself is a screen that will disagree with the rest of the app.
   */
  restriction: Restriction;
  restrictionNotice: RestrictionNotice | null;
  /**
   * May this person **do** this? Role, and not suspended.
   *
   * What almost every caller wants: it guards the buttons, the row actions and the panels, and a
   * suspension removes them the same way a role does.
   */
  can: (permission: Permission) => boolean;
  /**
   * May this person **reach** this? Role only.
   *
   * A soft suspension is read-only, not a smaller application — "they may still login and
   * navigate as usual, they may just see". So the rail and the route guards ask this instead:
   * hiding Team from a suspended owner would take away the screen that shows *why* they are
   * suspended, and several screens in the rail are gated by an edit permission because that is
   * who has business opening them, not because opening them writes anything.
   */
  mayReach: (permission: Permission) => boolean;
  refresh: () => Promise<void>;
}

const NO_RESTRICTION: Restriction = {
  level: "none",
  scope: null,
  note: null,
  until: null,
  since: null,
  canSignIn: true,
  canWrite: true,
};

/**
 * A permission that changes something.
 *
 * The naming convention is the check: everything in the list ends in `.view` or it does not, and
 * the ones that do not are the writes. It mirrors the server, which decides the same question by
 * HTTP method — the two cannot be made identical, and they do not have to be. **The server is
 * what refuses.** This only removes the invitation, exactly as the role check above it does.
 */
const isWrite = (permission: Permission) => !permission.endsWith(".view");

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<MeResponse>("/auth/me"),
    retry: false,
    staleTime: 60_000,
  });

  const value = useMemo<SessionValue>(() => {
    const role = (data?.role ?? null) as Role | null;
    const restriction = data?.restriction ?? NO_RESTRICTION;
    return {
      loading: isLoading,
      user: data?.user ?? null,
      org: data?.org ?? null,
      role,
      orgs: data?.orgs ?? [],
      plan: data?.plan ?? null,
      listingsUrl: data?.listingsUrl ?? "",
      consoleUrl: data?.consoleUrl ?? "",
      modules: data?.modules?.length ? data.modules : ["letting"],
      vocab: data?.vocabulary ?? DEFAULT_VOCABULARY,
      restriction,
      restrictionNotice: data?.restrictionNotice ?? null,
      /* One check, so a suspension removes what it forbids everywhere the app already removes
         what a role forbids — every `Require`, every `Can`, every button guarded by this. The
         alternative is disabling controls screen by screen, which is a suggestion rather than a
         restriction and misses the one screen nobody remembered. */
      can: (permission) =>
        role ? roleCan(role, permission) && !(!restriction.canWrite && isWrite(permission)) : false,
      mayReach: (permission) => (role ? roleCan(role, permission) : false),
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
      },
    };
  }, [data, isLoading, queryClient]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

/** Convenience for the many components that only need the agency's nouns and currency. */
export function useOrg() {
  const { org, vocab } = useSession();
  return { org, vocab };
}
